/**
 * 69.13 (a), DECISIONS 231 (the owner: "I think we can do 3 towed guns for now, to match the 3 round salvo … Artillery
 * should fit in shipping container, maybe multiple in a container"; "Light howitzer at 1000 people range isn't that crazy,
 * can run with that"): the towed gun's look — a light howitzer, game-sized. Two wheels on a short axle, the saddle and
 * the cradle on its trunnions (it elevates), the barrel with its recoil cylinders, breech and muzzle brake, and split box
 * trails with their spades and the lunette (the towing eye) where they meet.
 *
 * **Its poses** (`poseTowedGun`), from packed to deployed:
 *   - **packed:** the trails closed, the barrel swung over them (traversed half a turn, level), so it folds to
 *     TOWED_GUN.folded long — two stand end to end in a frame (the container family's 7.2 m clear: rendering/orbitShips.ts
 *     FRAME), one in the dropship's troop container;
 *   - **hitched:** packed, the trails' end raised to the tow's hitch (the lunette on the technical's or the truck's bumper);
 *   - **deploying:** the trails spread to TOWED_GUN.spread each side and their spades down, the barrel swung round to the
 *     front and raised to its elevation (the strike's 60°; 45° a fair-game alternative, the owner);
 *   - **firing:** the barrel recoils along its cradle; a muzzle anchor for the flash.
 * Metres, a person 1.8 tall; +z the muzzle's way deployed, the trails toward −z. A full tier (the parts, baked by the
 * host) and a far tier (a box and the barrel). The sheet: design_sheets/towed_gun.html. Not in play yet (the owner reviews
 * the sheet first).
 */
import * as THREE from 'three';
import type { UnitPalette } from './unitPalette';
import { box, group, anchor, mat } from './vehicleModels';

const RUBBER = '#1d1e1f', STEEL = '#5e615e';

/**
 * The gun's measures (metres): wheel radius, track (wheel centre to centre), the axle's height, the trunnion over it, the
 * barrel's length and radius, each trail's length, the trails' spread deployed (radians each side), the lunette's height
 * hitched, the folded length and width (what a frame's clear must take), the elevation it lays at.
 */
export const TOWED_GUN = {
  wheelR: 0.42, wheelW: 0.2, track: 1.32, axleY: 0.42, trunnionY: 1.0, trunnionZ: 0.05,
  barrel: 2.7, barrelR: 0.06, breechZ: -0.5, trail: 2.55, spread: 0.42, hitchY: 0.55,
  folded: 3.4, width: 1.55, elevation: Math.PI / 3,
} as const;

export interface TowedGunPose {
  /** 0 packed … 1 deployed (the trails spread, the barrel swung to the front and raised). */
  deploy: number;
  /** Hitched to a tow (packed only): the trails' end at the hitch's height. */
  hitched?: boolean;
  /** The barrel's elevation deployed (radians; TOWED_GUN.elevation by default). */
  elevation?: number;
  /** The barrel's recoil, 0 … 1 (a shot's kick). */
  recoil?: number;
}

export interface TowedGunModel {
  root: THREE.Group; full: THREE.Group; far: THREE.Group;
  /** The parts that move: the swing (the barrel's traverse over the trails), the cradle (its elevation), the barrel (its recoil), the trails, the spades. */
  swing: THREE.Group; cradle: THREE.Group; barrel: THREE.Group; trails: [THREE.Group, THREE.Group]; spades: [THREE.Group, THREE.Group];
  /** The muzzle (for the flash) and the lunette (the towing eye: where the tow's hitch holds it). */
  muzzle: THREE.Object3D; lunette: THREE.Object3D;
}

function cylinder(parent: THREE.Object3D, r: number, len: number, color: string, x: number, y: number, z: number, axis: 'x' | 'y' | 'z', seg = 10): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, seg), mat(color));
  if (axis === 'x') m.rotation.z = Math.PI / 2; else if (axis === 'z') m.rotation.x = Math.PI / 2;
  m.position.set(x, y, z); parent.add(m); return m;
}

/** The gun, packed (pose it with `poseTowedGun`). */
export function buildTowedGun(p: UnitPalette): TowedGunModel {
  const G = TOWED_GUN, root = new THREE.Group(), full = group(root), far = group(root);
  // the wheels, their hubs and the axle
  for (const side of [-1, 1]) {
    cylinder(full, G.wheelR, G.wheelW, RUBBER, side * G.track / 2, G.axleY, 0, 'x', 14);
    cylinder(full, G.wheelR * 0.55, G.wheelW + 0.02, STEEL, side * G.track / 2, G.axleY, 0, 'x', 8);
  }
  box(full, G.track - G.wheelW, 0.14, 0.18, p.dark, 0, G.axleY, 0);
  // the saddle on the axle, the trunnions up from it
  box(full, 0.62, 0.36, 0.7, p.dark, 0, G.axleY + 0.24, 0.02);
  for (const side of [-1, 1]) box(full, 0.08, G.trunnionY - G.axleY - 0.2, 0.3, p.body, side * 0.3, (G.trunnionY + G.axleY + 0.2) / 2, G.trunnionZ);
  // the swing (the top carriage's traverse, a half turn packed) and the cradle on it (the elevation)
  const swing = group(full, 0, G.trunnionY, G.trunnionZ);
  const cradle = group(swing);
  box(cradle, 0.34, 0.24, 1.5, p.body, 0, 0, 0.25);                                         // the cradle
  for (const side of [-1, 1]) cylinder(cradle, 0.065, 1.35, STEEL, side * 0.11, 0.17, 0.3, 'z', 8);   // the recoil cylinders
  // the barrel: its breech, the tube and the muzzle brake (it recoils along the cradle)
  const barrel = group(cradle);
  box(barrel, 0.28, 0.28, 0.42, p.metal, 0, 0, G.breechZ + 0.21);
  const tube = cylinder(barrel, G.barrelR, G.barrel, p.metal, 0, 0, G.breechZ + 0.42 + G.barrel / 2, 'z', 10);
  const brakeZ = G.breechZ + 0.42 + G.barrel;
  const brake = box(barrel, 0.2, 0.16, 0.26, p.metal, 0, 0, brakeZ - 0.13);
  tube.userData.thin = true; brake.userData.thin = true;   // (69.13 (a) in play: the tube and its brake no target, as every barrel)
  const muzzle = anchor(barrel, 0, 0, brakeZ);
  // the trails: two box legs from beside the saddle to their spades, the lunette on the left one's end
  const trails: THREE.Group[] = [], spades: THREE.Group[] = [];
  for (const side of [-1, 1]) {
    const t = group(full, side * 0.2, G.axleY + 0.06, -0.12);
    box(t, 0.16, 0.2, G.trail, p.body, 0, 0, -G.trail / 2);
    box(t, 0.18, 0.04, G.trail * 0.6, p.dark, 0, 0.12, -G.trail * 0.55);   // (the top plate)
    const spade = group(t, 0, 0, -G.trail + 0.12);
    box(spade, 0.42, 0.34, 0.05, p.dark, 0, -0.15, 0);
    trails.push(t); spades.push(spade);
  }
  const lunetteParent = trails[0];
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.07, 0.022, 6, 10), mat(STEEL)); ring.rotation.y = Math.PI / 2; ring.position.set(0, 0.02, -G.trail - 0.05);
  lunetteParent.add(ring);
  const lunette = anchor(lunetteParent, 0, 0.02, -G.trail - 0.05);
  // far tier: the wheels' block and the barrel
  box(far, G.track, G.axleY * 2, 0.9, p.dark, 0, G.axleY, 0);
  const farBarrel = box(far, 0.2, 0.2, G.barrel, p.metal, 0, G.trunnionY, G.barrel / 2 - 0.4);
  farBarrel.name = 'far:barrel';
  far.visible = false;
  // 69.13 (a) in play: the hit-shape fit's parts (rendering/hitShapeFit.ts fitTowedGun) — each trail with its spade, the carriage the rest
  full.traverse(o => { if ((o as THREE.Mesh).isMesh && !o.userData.thin) o.userData.fitPart = 'carriage'; });
  trails.forEach((t, i) => t.traverse(o => { if ((o as THREE.Mesh).isMesh) o.userData.fitPart = i === 0 ? 'trailL' : 'trailR'; }));
  const m: TowedGunModel = { root, full, far, swing, cradle, barrel, trails: trails as [THREE.Group, THREE.Group], spades: spades as [THREE.Group, THREE.Group], muzzle, lunette };
  poseTowedGun(m, { deploy: 0 });
  return m;
}

const ease = (x: number) => { const t = Math.max(0, Math.min(1, x)); return t * t * (3 - 2 * t); };

/**
 * Pose the gun (`deploy` 0 packed … 1 deployed): the trails spread over the first half (their ends to the ground, or
 * packed and hitched, raised to the hitch), the barrel swung from over the trails to the front over the middle, raised to
 * its elevation over the last third; the spades down once spread; the recoil along the cradle.
 */
export function poseTowedGun(m: TowedGunModel, pose: TowedGunPose): void {
  const G = TOWED_GUN, d = Math.max(0, Math.min(1, pose.deploy));
  const spread = ease(d / 0.5) * G.spread, swing = ease((d - 0.25) / 0.45), raise = ease((d - 0.65) / 0.35);
  // the trails' pitch: their end on the ground (deployed or packed standing) or at the hitch (packed and hitched)
  const endY = pose.hitched && d === 0 ? G.hitchY : 0.08, rootY = G.axleY + 0.06;
  const pitch = Math.atan2(rootY - endY, G.trail);   // (+: the end below the root)
  m.trails.forEach((t, i) => {
    const side = i === 0 ? -1 : 1;
    t.rotation.order = 'YXZ';
    t.rotation.set(-pitch, side * -spread, 0);   // (the end toward −z: a +x rotation lifts it; spread outward)
  });
  m.spades.forEach(s => { s.rotation.x = -ease((d - 0.4) / 0.2) * 0.9; });
  // the barrel: over the trails packed (a half turn, level, a little down to rest on them), to the front, then raised
  m.swing.rotation.y = Math.PI * (1 - swing);
  const elevation = (pose.elevation ?? G.elevation) * raise + (1 - swing) * 0.05;
  m.cradle.rotation.x = -elevation;
  m.barrel.position.z = -0.55 * Math.max(0, Math.min(1, pose.recoil ?? 0));
}

/** The gun's triangles at its full tier. */
export function towedGunTriangles(m: TowedGunModel): number {
  let n = 0;
  m.full.traverse(o => { const g = (o as THREE.Mesh).isMesh ? (o as THREE.Mesh).geometry : null; if (g) n += g.index ? g.index.count / 3 : g.attributes.position.count / 3; });
  return n;
}
